import { RichTextFieldStrapiProps } from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { SideNavigationHeadingSection } from '@/features/blog/SideNavigation/types';
import { awkwardSymbolReplacementMap, hashPrefix } from './consts';

export function getHeadingsText(data: RichTextFieldStrapiProps[]) {
  const headingsText: string[] = [];

  data.forEach((slice) => {
    if (!slice?.text) return;

    slice.text.forEach((content) => {
      if (content.type === 'heading' && content.level === 2) {
        const combinedH2 = content.children
          .map((child) => ('text' in child ? child?.text : ''))
          .join(' ');
        headingsText.push(combinedH2);
      }
    });
  });

  return headingsText;
}

export function getRichTextFieldHeadingLevel2(data: RichTextFieldStrapiProps) {
  const headingsText: string[] = [];

  [data?.text, data?.text2].forEach((block) => {
    if (Array.isArray(block)) {
      block.forEach((content) => {
        if (content.type === 'heading' && content.level === 2) {
          const combinedH2 = content.children
            .map((child) => ('text' in child ? child?.text : ''))
            .join(' ');
          headingsText.push(combinedH2);
        }
      });
    } else if (typeof block === 'string') {
      const matches = block.match(/^##\s+(.*)$/gm); // Finds all Markdown level 2 headings (lines starting with '## ') in the string.

      if (matches) {
        matches.forEach((heading) => {
          headingsText.push(heading.replace(/^##\s+/, '').trim());
        });
      }
    }
  });

  return headingsText;
}

export function sanitizeSideNavText(text: string) {
  return text.replaceAll(`\\`, '');
}

/**
 * Some obscure regex to handle edge cases like /_-|\ and similar.
 */
export function unescapeMarkdownHeadingEscapes(text: string) {
  return text.replace(/\\([!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~])/g, '$1');
}

export function transformUrlHashToText(dirtyText: string) {
  const preCleanText = dirtyText.slice(hashPrefix.length + 2); // +2 for the dash and the # char

  try {
    let cleanedText = decodeURIComponent(preCleanText);

    awkwardSymbolReplacementMap.toReversed().forEach(({ human, machine }) => {
      cleanedText = cleanedText.replaceAll(machine, human);
    });

    return cleanedText.toLowerCase();
  } catch (e) {
    let cleanedText = dirtyText.slice(hashPrefix.length + 2);

    awkwardSymbolReplacementMap.toReversed().forEach(({ human, machine }) => {
      cleanedText = cleanedText.replaceAll(machine, human);
    });
    return cleanedText.toLowerCase();
  }
}

export function trasnformTextToUrlHash(text: string) {
  try {
    let transformedTextToURLHash = `#${hashPrefix}-${decodeURIComponent(text)}`;

    awkwardSymbolReplacementMap.forEach(({ human, machine }) => {
      transformedTextToURLHash = transformedTextToURLHash.replaceAll(
        human,
        machine
      );
    });

    return transformedTextToURLHash.toLowerCase();
  } catch (e) {
    let transformedTextToURLHash = `#${hashPrefix}-${text}`;

    awkwardSymbolReplacementMap.forEach(({ human, machine }) => {
      transformedTextToURLHash = transformedTextToURLHash.replaceAll(
        human,
        machine
      );
    });

    return transformedTextToURLHash.toLowerCase();
  }
}

export function scrollEntryIntoView(
  scrollRoot: HTMLElement,
  rowElement: HTMLElement,
  marginPx: number
) {
  const rootSection = scrollRoot.getBoundingClientRect();
  const singleEntry = rowElement.getBoundingClientRect();

  if (
    singleEntry.top >= rootSection.top + marginPx &&
    singleEntry.bottom <= rootSection.bottom - marginPx
  ) {
    return;
  }

  let scrollCorrectionPx = 0;

  const isNearTop = singleEntry.top < rootSection.top + marginPx;

  if (isNearTop) {
    scrollCorrectionPx = singleEntry.top - rootSection.top - marginPx;
  } else if (singleEntry.bottom > rootSection.bottom - marginPx) {
    scrollCorrectionPx = singleEntry.bottom - rootSection.bottom + marginPx;
  }

  scrollRoot.scrollTop += scrollCorrectionPx;
}

export function scrollToPosition(x: number, y: number) {
  window.scrollTo({ left: x, top: y, behavior: 'smooth' });
}

export function normalizeHeadingForMatch(value: string) {
  return unescapeMarkdownHeadingEscapes(value).replace(/\s/g, '').toLowerCase();
}

export function getSideNavigationElementsAsSections(
  headingElements: HTMLHeadingElement[]
): SideNavigationHeadingSection[] {
  return headingElements.reduce((acc, element, index) => {
    const text = unescapeMarkdownHeadingEscapes(element.innerText);
    const id = `${text}-${index}`;
    if (element.tagName === 'H2') {
      acc.push({
        tag: 'H2',
        id,
        text,
        children: [],
      });
    } else {
      acc.at(-1)?.children.push({
        tag: 'H3',
        id,
        text,
      });
    }
    return acc;
  }, [] as SideNavigationHeadingSection[]);
}

export function scrollToHeadingElement(
  text: string,
  headingElements: HTMLHeadingElement[]
) {
  let targetElement: HTMLHeadingElement | null = null;

  headingElements.forEach((element) => {
    if (
      normalizeHeadingForMatch(element.innerText) ===
      normalizeHeadingForMatch(text)
    ) {
      targetElement = element as HTMLHeadingElement;
    }
  });

  if (targetElement) {
    // we want to scroll to the top of the element (10% from top of the screen)
    const offset =
      (targetElement as HTMLHeadingElement).offsetTop -
      window.innerHeight * 0.1;

    scrollToPosition(0, offset);
  }
}
